// Screen D / E — Desktop Landing Page
// Reference: Figma node 7035-202313 ("landing_page_Desktop")
// Reference: TECHNICAL DESIGN DOCUMENT — Section 7.3
//
// "When the link is accessed via desktop, the system directly navigates to the
//  App Download landing page. This page displays a QR Code for mobile
//  installation, along with direct store links for users without the
//  application."
//
// Design overview (1440 × ~960 native, fully responsive on narrower viewports):
//   1. Sticky top nav (#0186CE) with Principal+CIMB brand + 5 menu items +
//      "Log In & Digital Service" CTA + search.
//   2. Hero illustration (desktop-hero.png) — contains marketing copy,
//      phone mockups, NDID + 7-Eleven feature blocks, the QR code image,
//      and the two store badges. Invisible click overlays on the store badge
//      regions make them act as real CTAs.
//   3. Decorative bottom strip (desktop-footer.png) — curved blue waves.

import React from "react";
import { FIREBASE_EVENTS, PLATFORMS, STORE_URLS } from "./constants";
import { trackEvent } from "./api";

function ScreenDesktopPublic({ destination, resolveResult, query }) {
  const iosUrl = resolveResult?.store_url_ios || STORE_URLS.IOS;
  const androidUrl = resolveResult?.store_url_android || STORE_URLS.ANDROID;

  const onStoreClick = (storePlatform, e) => {
    if (e) e.preventDefault();
    trackEvent({
      event_name: FIREBASE_EVENTS.APP_STORE_REDIRECT,
      destination,
      platform: PLATFORMS.DESKTOP,
      ref_id: query?.ref_id,
    });
    const target = storePlatform === PLATFORMS.IOS ? iosUrl : androidUrl;
    window.open(target, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="dl-desktop">
      {/* ---------- Top navigation bar ---------- */}
      <header className="dl-desktop__header">
        <div className="dl-desktop__header-inner">
          <a
            className="dl-desktop__brand"
            href="https://www.principal.th"
            aria-label="Principal TH"
          >
            <img
              className="dl-desktop__brand-principal"
              src="/deeplink/principal-only.png"
              srcSet="/deeplink/principal-only.png 1x, /deeplink/principal-only@2x.png 2x, /deeplink/principal-only@3x.png 3x"
              alt="Principal"
            />
            <span className="dl-desktop__brand-divider" aria-hidden="true" />
            <span
              className="dl-desktop__brand-cimb"
              aria-label="In alliance with CIMB"
            >
              {/* "In alliance with CIMB" — Figma node 7186:144697 */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="99"
                height="10"
                viewBox="0 0 99 10"
                fill="none"
              >
                <path d="M81.3079 6.32069C80.9315 6.60574 80.4609 6.79578 79.9904 6.79578C78.8611 6.79578 78.0142 5.8456 78.0142 4.70538C78.0142 3.56517 78.8611 2.615 79.9904 2.615C80.4609 2.615 80.9315 2.80503 81.3079 3.09008L82.1549 2.23492C81.5902 1.75983 80.8374 1.37976 79.9904 1.37976C78.2024 1.37976 76.6967 2.90005 76.6967 4.70538C76.6967 6.51072 78.2024 8.03101 79.9904 8.03101C80.7433 8.03101 81.4961 7.74596 82.1549 7.17585L81.3079 6.32069ZM83.3783 7.84097H84.6017V1.5698H83.3783V7.84097ZM89.5893 5.56055C89.5893 5.46553 87.4249 1.5698 87.4249 1.5698H86.2015V7.84097H87.4249V4.13528C87.519 4.32531 89.5893 7.93599 89.5893 7.93599C89.5893 7.93599 91.6597 4.32531 91.7538 4.13528V7.84097H92.9772V1.5698H91.7538C91.7538 1.5698 89.5893 5.46553 89.5893 5.56055ZM96.8356 6.60574H95.6122V5.08546H96.8356C97.4002 5.08546 97.5884 5.46553 97.5884 5.8456C97.5884 6.22567 97.4002 6.60574 96.8356 6.60574ZM95.6122 2.615H96.7415C97.212 2.615 97.4943 2.90005 97.4943 3.28012C97.4943 3.75521 97.212 3.94524 96.7415 3.94524H95.6122V2.615ZM98.1531 4.61037C98.4354 4.42033 98.9059 4.04026 98.9059 3.28012C98.9059 2.23492 98.1531 1.47478 97.0238 1.47478H94.4829V7.74595H97.1179C98.3413 7.74595 99 7.08083 99 5.94062C98.9059 5.18048 98.4354 4.8004 98.1531 4.61037Z" fill="white"/>
                <path d="M0.846965 1.37927V7.65045H0V1.37927H0.846965Z" fill="white"/>
                <path d="M2.16466 4.32512C2.16466 3.85003 2.16466 3.46996 2.16466 3.08989H2.91752V3.85003C3.10574 3.37494 3.67038 2.99487 4.42323 2.99487C5.08198 2.99487 6.02306 3.37494 6.02306 4.89523V7.55572H5.17609V4.99025C5.17609 4.2301 4.89377 3.66 4.14091 3.66C3.57627 3.66 3.19984 4.04007 3.01163 4.51516C3.01163 4.61017 2.91752 4.80021 2.91752 4.89523V7.65074H2.07056V4.32512H2.16466Z" fill="white"/>
                <path d="M13.7275 7.70115L13.6334 7.13105C13.3511 7.51112 12.8805 7.79617 12.2218 7.79617C11.2807 7.79617 10.9043 7.13105 10.9043 6.46593C10.9043 5.42073 11.8454 4.7556 13.6334 4.7556V4.66059C13.6334 4.28052 13.5393 3.6154 12.5982 3.6154C12.2218 3.6154 11.7513 3.71041 11.4689 3.99546L11.2807 3.42536C11.6572 3.14031 12.2218 3.04529 12.7864 3.04529C14.198 3.04529 14.4804 3.99547 14.4804 4.85062V6.56094C14.4804 6.94101 14.4804 7.32108 14.5745 7.60613H13.7275V7.70115ZM13.5393 5.32571C12.6923 5.32571 11.6572 5.42073 11.6572 6.37091C11.6572 6.94101 12.0336 7.13105 12.41 7.13105C12.9747 7.13105 13.3511 6.75098 13.4452 6.37091C13.4452 6.27589 13.4452 6.18087 13.4452 6.08585V5.32571H13.5393Z" fill="white"/>
                <path d="M15.5172 1.05029H16.3642V7.70153H15.5172V1.05029Z" fill="white"/>
                <path d="M17.5032 1.00024H18.3501V7.65148H17.5032V1.00024Z" fill="white"/>
                <path d="M20.6079 1.85607C20.6079 2.14112 20.4197 2.33116 20.1374 2.33116C19.8551 2.33116 19.6669 2.14112 19.6669 1.85607C19.6669 1.57102 19.8551 1.38098 20.1374 1.38098C20.4197 1.38098 20.6079 1.57102 20.6079 1.85607ZM19.761 7.65215V3.18632H20.6079V7.65215H19.761Z" fill="white"/>
                <path d="M24.3724 7.65196L24.2783 7.08185C23.996 7.46192 23.5254 7.74697 22.8667 7.74697C21.9256 7.74697 21.5492 7.08185 21.5492 6.41673C21.5492 5.37153 22.4903 4.70641 24.2783 4.70641V4.61139C24.2783 4.23132 24.1842 3.5662 23.2431 3.5662C22.8667 3.5662 22.3962 3.66122 22.1138 3.94627L21.9256 3.37617C22.3021 3.09111 22.8667 2.99609 23.4313 2.99609C24.8429 2.99609 25.1253 3.94627 25.1253 4.80143V6.51174C25.1253 6.89181 25.1253 7.27188 25.2194 7.55694H24.3724V7.65196ZM24.2783 5.27652C23.4313 5.27652 22.3962 5.37153 22.3962 6.32171C22.3962 6.89181 22.7726 7.08185 23.149 7.08185C23.7137 7.08185 24.0901 6.70178 24.1842 6.32171C24.1842 6.22669 24.1842 6.13167 24.1842 6.03666V5.27652H24.2783Z" fill="white"/>
                <path d="M26.3485 4.32634C26.3485 3.85125 26.3485 3.47118 26.3485 3.09111H27.1014V3.85125C27.2896 3.37617 27.8542 2.99609 28.6071 2.99609C29.2658 2.99609 30.2069 3.37616 30.2069 4.89645V7.55694H29.3599V4.99147C29.3599 4.23132 29.0776 3.66122 28.3247 3.66122C27.7601 3.66122 27.3837 4.04129 27.1955 4.51638C27.1955 4.6114 27.1014 4.80143 27.1014 4.89645V7.65196H26.2544V4.32634H26.3485Z" fill="white"/>
                <path d="M34.6308 7.462C34.4425 7.55701 33.972 7.74705 33.3133 7.74705C31.9958 7.74705 31.0547 6.79687 31.0547 5.46663C31.0547 4.04136 31.9958 3.09119 33.4074 3.09119C33.8779 3.09119 34.3484 3.1862 34.5366 3.28122L34.3484 3.94634C34.1602 3.85133 33.8779 3.75631 33.4074 3.75631C32.3722 3.75631 31.8075 4.51645 31.8075 5.46663C31.8075 6.51182 32.4663 7.17694 33.3133 7.17694C33.7838 7.17694 34.0661 7.08193 34.3484 6.98691L34.6308 7.462Z" fill="white"/>
                <path d="M35.9143 5.56059C35.9143 6.70081 36.6672 7.08087 37.4201 7.08087C37.9847 7.08087 38.3611 6.98586 38.6435 6.89084L38.7376 7.46095C38.4552 7.55596 37.9847 7.746 37.2318 7.746C35.8202 7.746 35.0674 6.79582 35.0674 5.46558C35.0674 4.13533 35.8202 2.99512 37.1377 2.99512C38.5493 2.99512 39.0199 4.32536 39.0199 5.08551C39.0199 5.27554 39.0199 5.37056 39.0199 5.46558H35.9143V5.56059ZM38.3611 4.89547C38.3611 4.42038 38.1729 3.56523 37.2318 3.56523C36.3849 3.56523 36.0085 4.32536 36.0085 4.89547H38.3611Z" fill="white"/>
                <path d="M44.511 3.09119L45.0757 5.37161C45.1698 5.8467 45.358 6.32178 45.4521 6.79687C45.5462 6.32178 45.7344 5.8467 45.8285 5.37161L46.5814 3.09119H47.2401L47.8989 5.37161C48.0871 5.94171 48.1812 6.4168 48.2753 6.79687C48.3694 6.32178 48.4635 5.8467 48.6518 5.37161L49.3105 3.09119H50.1575L48.7459 7.55701H47.993L47.3342 5.37161C47.146 4.89652 47.0519 4.42143 46.9578 3.85133C46.8637 4.42143 46.6755 4.89652 46.5814 5.37161L45.8285 7.55701H45.0757L43.6641 3.09119H44.511Z" fill="white"/>
                <path d="M51.8517 1.85558C51.8517 2.14063 51.6635 2.33067 51.3812 2.33067C51.0989 2.33067 50.9106 2.14063 50.9106 1.85558C50.9106 1.57053 51.0989 1.38049 51.3812 1.38049C51.6635 1.38049 51.8517 1.57053 51.8517 1.85558ZM50.9106 7.65166V3.18583H51.7576V7.65166H50.9106Z" fill="white"/>
                <path d="M54.1103 1.85583V3.18608H55.2396V3.85121H54.1103V6.32167C54.1103 6.89177 54.2985 7.17683 54.675 7.17683C54.8632 7.17683 55.0514 7.17683 55.1455 7.08181V7.65192C54.9573 7.74693 54.7691 7.74693 54.3927 7.74693C54.0162 7.74693 53.7339 7.65192 53.5457 7.36686C53.3575 7.08181 53.2634 6.70174 53.2634 6.22665V3.75619H52.6046V3.09107H53.2634V2.04587L54.1103 1.85583Z" fill="white"/>
                <path d="M56.1818 1.00049H57.0287V3.85102C57.1228 3.66098 57.4052 3.37593 57.5934 3.28091C57.8757 3.18589 58.158 3.09088 58.4403 3.09088C59.005 3.09088 60.0401 3.47095 60.0401 4.99123V7.65172H59.1932V5.08624C59.1932 4.3261 58.9109 3.756 58.158 3.756C57.5934 3.756 57.2169 4.13607 57.0287 4.61116C57.0287 4.70617 56.9346 4.80119 56.9346 4.99123V7.74674H56.0876V1.00049H56.1818Z" fill="white"/>
                <path d="M74 0H64V10H74V0Z" fill="white"/>
                <path d="M70.5065 2H65L67.3896 5H73L70.5065 2Z" fill="#ED1C24"/>
                <path d="M73 5H67.3896L65 8H70.5065L73 5Z" fill="#790008"/>
              </svg>
            </span>
          </a>

          <nav className="dl-desktop__nav" aria-label="หมวดหลัก">
            <a className="dl-desktop__nav-item" href="https://www.principal.th/fund-listing/th/all">
              กองทุนรวม
            </a>
            <a className="dl-desktop__nav-item" href="https://www.principal.th/provident-fund">
              กองทุนสำรองเลี้ยงชีพ
            </a>
            <a className="dl-desktop__nav-item" href="https://www.principal.th/private-fund">
              กองทุนส่วนบุคคล
            </a>
            <a className="dl-desktop__nav-item" href="https://www.principal.th/library">
              คลังความรู้
            </a>
            <a className="dl-desktop__nav-item" href="https://www.principal.th/about-us">
              เกี่ยวกับเรา
            </a>
            <a className="dl-desktop__nav-item" href="https://www.principal.th/login">
              Log In &amp; Digital Service
            </a>
            <button
              type="button"
              className="dl-desktop__search"
              aria-label="ค้นหา"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle cx="11" cy="11" r="7" stroke="white" strokeWidth="2" />
                <line
                  x1="16.5"
                  y1="16.5"
                  x2="21"
                  y2="21"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </nav>
        </div>
      </header>

      {/* ---------- Hero (marketing illustration) ---------- */}
      {/*
        Single PNG exported from Figma — contains the hero phone mockups, the
        marketing copy, and the NDID / 7-Eleven feature blocks. Purely visual.
      */}
      <section className="dl-desktop__hero">
        <img
          src="/deeplink/desktop-hero.png"
          alt="แอปพลิเคชัน Principal TH — เวอร์ชันใหม่ เปิดบัญชีง่าย"
          className="dl-desktop__hero-image"
          draggable={false}
        />
      </section>

      {/* ---------- Footer (QR + store badges + caption) ---------- */}
      {/*
        Bottom panel PNG that contains the QR code visual, the App Store /
        Google Play badges, the caption "เปิดบัญชีผ่านแอปพลิเคชัน Principal TH",
        and the curved bottom decoration. Two invisible click overlays sit on
        top of the badge regions so they act as real CTAs while keeping the
        visual perfectly on-spec. Coordinates are measured from the footer
        image (Figma image_83, native 1887×360) so the targets stay aligned
        at every viewport width.
      */}
      <section className="dl-desktop__footer">
        <img
          src="/deeplink/desktop-footer.png"
          alt=""
          className="dl-desktop__footer-image"
          draggable={false}
        />
        <button
          type="button"
          className="dl-desktop__store-overlay dl-desktop__store-overlay--ios"
          onClick={e => onStoreClick(PLATFORMS.IOS, e)}
          aria-label="Download on the App Store"
        />
        <button
          type="button"
          className="dl-desktop__store-overlay dl-desktop__store-overlay--android"
          onClick={e => onStoreClick(PLATFORMS.ANDROID, e)}
          aria-label="Get it on Google Play"
        />
      </section>
    </div>
  );
}

export default ScreenDesktopPublic;
